import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  OnInit,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { MatIcon } from '@angular/material/icon';
import { TranslatePipe } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { MeinProjekt } from '../../models/mein-projekt.model';
import { MeineProjekteService } from '../../services/meine-projekte.service';
import { DashboardLayoutService } from '../../services/dashboard-layout.service';
import { NotificationService } from '../../services/notification.service';
import { getProjectIconDef } from '../../utils/project-icon.utils';
import { HEUTE } from '../meine-aufgaben/meine-aufgaben.store';
import { laufZeitpunkt } from './lauf-zeitpunkt';
import { PROJEKTE_VERWALTEN_ID, ProjekteVerwaltenComponent } from './projekte-verwalten/projekte-verwalten.component';

type ProjekteStatus = 'laedt' | 'fehler' | 'bereit';
type BalkenStufe = 'hoch' | 'mittel' | 'niedrig';

interface LaufText {
  key: string;
  params?: Record<string, string | number>;
}

const namensfolge = new Intl.Collator('de', { sensitivity: 'base' });

@Component({
  selector: 'app-meine-projekte',
  imports: [MatIcon, TranslatePipe, ProjekteVerwaltenComponent],
  templateUrl: './meine-projekte.component.html',
  styleUrl: './meine-projekte.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '(document:click)': 'klickDaneben($event)',
    '(document:keydown.escape)': 'escape()',
  },
})
export class MeineProjekteComponent implements OnInit {
  private readonly projekteService = inject(MeineProjekteService);
  private readonly layoutService = inject(DashboardLayoutService);
  private readonly router = inject(Router);
  private readonly notification = inject(NotificationService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly jetzt = inject(HEUTE);

  protected readonly projekte = signal<MeinProjekt[]>([]);
  protected readonly status = signal<ProjekteStatus>('laedt');
  protected readonly verwaltenOffen = signal(false);
  protected readonly popoverId = PROJEKTE_VERWALTEN_ID;
  private readonly kopf = viewChild.required<ElementRef<HTMLElement>>('kopf');
  private readonly verwaltenKnopf = viewChild<ElementRef<HTMLButtonElement>>('verwaltenKnopf');

  protected readonly angeheftetIds = computed(() => new Set(this.layoutService.layout()?.angehefteteProjekte ?? []));
  protected readonly ausgeblendetIds = computed(
    () => new Set(this.layoutService.layout()?.ausgeblendeteProjekte ?? []),
  );

  protected readonly sichtbare = computed(() => {
    const angeheftet = this.angeheftetIds();
    const ausgeblendet = this.ausgeblendetIds();
    return this.projekte()
      .filter((p) => !ausgeblendet.has(p.projectId))
      .sort(
        (a, b) =>
          Number(angeheftet.has(b.projectId)) - Number(angeheftet.has(a.projectId)) ||
          namensfolge.compare(a.name, b.name),
      );
  });

  ngOnInit(): void {
    this.layoutService.laden().pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
    this.laden();
  }

  protected laden(): void {
    this.status.set('laedt');
    this.projekteService
      .laden()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (projekte) => {
          this.projekte.set(projekte);
          this.status.set('bereit');
        },
        error: () => this.status.set('fehler'),
      });
  }

  protected angeheftet(projectId: string): boolean {
    return this.angeheftetIds().has(projectId);
  }

  protected farbe(projekt: MeinProjekt): string {
    return getProjectIconDef(projekt).color;
  }

  protected schrift(projekt: MeinProjekt): string {
    return getProjectIconDef(projekt).onColor;
  }

  protected laufText(projekt: MeinProjekt): LaufText {
    const lauf = projekt.letzterLauf;
    if (!lauf) return { key: 'home.meineProjekte.keinLauf' };
    if (lauf.status === 'LAEUFT') return { key: 'home.meineProjekte.laeuft', params: { kennung: lauf.kennung } };
    if (lauf.status === 'FEHLGESCHLAGEN') {
      return { key: 'home.meineProjekte.fehlgeschlagen', params: { kennung: lauf.kennung } };
    }
    if (!lauf.endeAm) return { key: 'home.meineProjekte.keinLauf' };
    const zeit = laufZeitpunkt(new Date(lauf.endeAm), this.jetzt());
    return { key: `home.meineProjekte.zeit.${zeit.key}`, params: { n: zeit.n ?? 0 } };
  }

  protected balkenStufe(rate: number | null): BalkenStufe {
    if (rate === null || rate < 70) return 'niedrig';
    return rate >= 85 ? 'hoch' : 'mittel';
  }

  protected oeffnen(projectId: string): void {
    void this.router.navigate(projektZiel(projectId));
  }

  protected sternKlick(projectId: string): void {
    this.speichern(this.layoutService.toggleAngeheftet(projectId));
  }

  protected haekchenKlick(projectId: string): void {
    this.speichern(this.layoutService.toggleAusgeblendet(projectId));
  }

  protected verwaltenSchliessen(): void {
    this.verwaltenOffen.set(false);
    this.verwaltenKnopf()?.nativeElement.focus();
  }

  protected klickDaneben(event: MouseEvent): void {
    if (this.verwaltenOffen() && !event.composedPath().includes(this.kopf().nativeElement)) {
      this.verwaltenOffen.set(false);
    }
  }

  protected escape(): void {
    if (this.verwaltenOffen()) this.verwaltenSchliessen();
  }

  private speichern(auftrag: Observable<void>): void {
    auftrag
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({ error: () => this.notification.error('home.meineProjekte.speicherFehler') });
  }
}

function projektZiel(projectId: string): string[] {
  return ['/projects', projectId];
}
